import React, { useState, useEffect, memo } from "react";
import EditIcon from "@material-ui/icons/Edit";
import {
  saveDictationMedCodes,
  updateMedCodingRecommendation,
} from "apiHandler/actions";
import useUpdateEffect from "components/customHooks/useUpdateEffect";

import { useUserData } from "components/customHooks/userDataContext";
import ButtonProgress from "components/ButtonProgress";
import ShowText from "components/modals/ShowText";
import { Box, Button, LinearProgress, Typography } from "@mui/material";
import MedCodesGrid from "./medCodesGrid";
import AddCodesDialog from "./addCodesDialog";

const getInitialArray = (count) => {
  let response = [];
  for (let i = 1; i <= count; i++) {
    response.push({
      id: i,
      code: "",
      saved: false,
      isSuggested: false,
      value: {},
      description: "",
    });
  }
  return response;
};

const DIAGNOSIS_COUNT = 12;
const PROCEDURE_COUNT = 12;
const MODIFIER_COUNT = 4;

const NonAiMedCodes = ({
  medCodes,
  suggDiag,
  suggProce,
  loading,
  details,
  medCodeError,
  readOnly,
}) => {
  console.log("medCodes: ", medCodes);
  // console.log('details---', details);
  const [edit, setEdit] = useState(false);
  const [showText, setShowText] = useState(false);
  const [showLinkText, setShowLinkText] = useState(false);
  const [addType, setAddType] = useState(null);
  const [savedDiag, setSavedDiag] = useState(getInitialArray(DIAGNOSIS_COUNT));
  const [savedProce, setSavedProce] = useState(
    getInitialArray(PROCEDURE_COUNT),
  );
  const [savedModifier, setSavedModifier] = useState(
    getInitialArray(MODIFIER_COUNT),
  );
  const [isSaving, setIsSaving] = useState(false);
  const userData = useUserData();
  const token = userData?.access_token;

  // set savedDiag and savedProce arrays when medCodes changes
  useEffect(() => {
    let dCodes = [],
      pCodes = [],
      recommended = [];
    for (let i = 0; i < medCodes.length; i++) {
      if (medCodes[i]?.code_type?.toUpperCase().startsWith("ICD"))
        dCodes.push(medCodes[i]);
      else if (medCodes[i]["code_type"] === "CPT") pCodes.push(medCodes[i]);
      else if (
        Object.prototype.hasOwnProperty.call(medCodes[i], "Recommended_Codes")
      )
        recommended = medCodes[i]["Recommended_Codes"];
    }
    if (recommended && recommended?.length === 0) setEdit(true);
    const dCodeLength = dCodes.length;
    const pCodeLength = pCodes.length;
    const suggestedDiag = recommended.filter(
      (c) => c.code_type?.toUpperCase().startsWith("ICD") && c.status === "New",
    );
    const suggestedProce = recommended.filter(
      (c) => c.code_type === "CPT" && c.status === "New",
    );
    setSavedDiag(
      savedDiag.map((s, i) => {
        if (dCodes?.[i]?.code) {
          const { code, status, diagnosis_description } = dCodes[i];
          if (status === "Active")
            return {
              id: s.id,
              code: code,
              saved: true,
              isSuggested: false,
              description: diagnosis_description,
            };
          if (status === "Reject") return s;
          return {
            id: s.id,
            code: code,
            saved: true,
            isSuggested: false,
            description: diagnosis_description,
          };
        } else if (suggestedDiag?.[i - dCodeLength]?.code) {
          const index = i - dCodeLength;
          const matchedPatternArray =
            suggestedDiag[index]?.["Pattern_Matched"]?.split(" ");
          let description;
          for (let x = 0; x < matchedPatternArray.length; x++) {
            const regex = new RegExp(matchedPatternArray[x], "gi");
            description = suggestedDiag[index]?.["Description"]?.replace(
              regex,
              `<b>${matchedPatternArray[x]}</b>`,
            );
          }
          return {
            id: s.id,
            code: suggestedDiag[index]?.["code"],
            saved: false,
            isSuggested: true,
            recommendation_id: suggestedDiag[index]?.["recommendation_id"],
            description: description,
          };
        } else {
          return s;
        }
      }),
    );
    setSavedProce(
      savedProce.map((s, i) => {
        if (pCodes?.[i]?.code) {
          const { code, status, procedure_description } = pCodes[i];
          if (status === "Active")
            return {
              id: s.id,
              code: code,
              saved: true,
              isSuggested: false,
              description: procedure_description,
            };
          if (status === "Reject") return s;
          return {
            id: s.id,
            code: code,
            saved: true,
            isSuggested: false,
            description: procedure_description,
          };
        } else if (suggestedProce?.[i - pCodeLength]?.code) {
          const index = i - pCodeLength;
          const matchedPatternArray =
            suggestedProce[index]?.["Pattern_Matched"]?.split(" ");
          let description;
          for (let x = 0; x < matchedPatternArray.length; x++) {
            const regex = new RegExp(matchedPatternArray[x], "gi");
            description = suggestedProce[index]?.["Description"]?.replace(
              regex,
              `<b>${matchedPatternArray[x]}</b>`,
            );
          }
          return {
            id: s.id,
            code: suggestedProce[index]?.["code"],
            saved: false,
            isSuggested: true,
            recommendation_id: suggestedProce[index]?.["recommendation_id"],
            description: description,
          };
        } else {
          return s;
        }
      }),
    );
  }, [medCodes]);

  // set savedDiag and savedProce arrays when suggested codes is changed
  useUpdateEffect(() => {
    setEdit(true);
    updateAsPerSuggested("Diagnosis", savedDiag, setSavedDiag, suggDiag);
  }, [suggDiag]);

  useUpdateEffect(() => {
    setEdit(true);
    updateAsPerSuggested("Procedure", savedProce, setSavedProce, suggProce);
  }, [suggProce]);

  const updateAsPerSuggested = (type, array, setArray, suggestedCodes) => {
    let i = 0;
    setArray(
      array.map((s) => {
        if (s?.code && !s?.isSuggested)
          return s; // if code is there or code is not suggested then return same
        else if (suggestedCodes[i]) {
          const index = i;
          const matchedPatternArray =
            suggestedCodes[index]?.["Pattern_Matched"]?.split(" ");
          let description;
          for (let x = 0; x < matchedPatternArray.length; x++) {
            const regex = new RegExp(matchedPatternArray[x], "gi");
            description = suggestedCodes[index]?.["Description"]?.replace(
              regex,
              `<b>${matchedPatternArray[x]}</b>`,
            );
          }
          i++;
          return {
            id: s.id,
            code: suggestedCodes[index]?.["code"],
            saved: false,
            isSuggested: true,
            recommendation_id: suggestedCodes[index]?.["recommendation_id"],
            description: description,
          };
        } else {
          return s;
        }
      }),
    );
  };

  // To update status of the suggested codes
  const updateSuggestedCodeStatus = async (
    type,
    id,
    status,
    recommendation_id,
  ) => {
    const payload = {
      type: "Diagnosis",
      record_data: {
        recommendation_id: recommendation_id,
        status: status,
        scribe_id: userData.email,
      },
    };
    const response = await updateMedCodingRecommendation(payload);
    if (response && response !== "ERROR") {
      if (type === "Diagnosis") {
        setSavedDiag(
          savedDiag.map((s) => {
            if (s.id !== id) return s;
            if (status === "Active")
              return { ...s, saved: true, isSuggested: false };
            return {
              id: id,
              code: "",
              saved: false,
              isSuggested: false,
              value: {},
              description: "",
            };
          }),
        );
      } else if (type === "Procedure") {
        setSavedProce(
          savedProce.map((s) => {
            if (s.id !== id) return s;
            if (status === "Active")
              return { ...s, saved: true, isSuggested: false };
            return {
              id: id,
              code: "",
              saved: false,
              isSuggested: false,
              value: {},
              description: "",
            };
          }),
        );
      }
    }
  };

  // To delete the code (locally)
  const onDelete = (type, id) => {
    if (type === "Diagnosis") {
      setSavedDiag(
        savedDiag.map((s) => {
          if (s.id !== id) return s;
          return {
            id: id,
            code: "",
            saved: false,
            isSuggested: false,
            value: {},
            description: "",
          };
        }),
      );
    }
    if (type === "Procedure") {
      setSavedProce(
        savedProce.map((s) => {
          if (s.id !== id) return s;
          return {
            id: id,
            code: "",
            saved: false,
            isSuggested: false,
            value: {},
            description: "",
          };
        }),
      );
    }
    if (type === "Modifier") {
      setSavedModifier(
        savedModifier.map((s) => {
          if (s.id !== id) return s;
          return {
            id: id,
            code: "",
            saved: false,
            isSuggested: false,
            value: {},
            description: "",
          };
        }),
      );
    }
  };

  // To save all the local changes
  const onSave = async () => {
    setIsSaving(true);
    const payload = {
      type: "Dictation",
      record_data: {
        practice_id: details.practice_id,
        encounter_id: details.encounter_id,
        npi: details.npi,
        dictation_id: details.id,
        scribe_id: userData.email,
        diag_codes: savedDiag
          .map((s) => {
            if (s.saved) return s.code;
            return null;
          })
          .filter((x) => x),
        proc_codes: savedProce
          .map((s) => {
            if (s.saved) return s.code;
            return null;
          })
          .filter((x) => x),
        modifier1: savedModifier[0].code,
        modifier2: savedModifier[1].code,
        modifier3: savedModifier[2].code,
        modifier4: savedModifier[3].code,
      },
    };
    await saveDictationMedCodes(payload, token);
    setEdit(false);
    setIsSaving(false);
  };

  const buildRows = (saved, suggested, type) => [
    ...saved
      .filter((x) => x.code)
      .map((x, i) => ({
        id: `${type}-S-${i}`,
        code: x.code,
        description: x.description,
        status: "Saved",
        raw: x,
      })),
    ...suggested.map((x, i) => ({
      id: `${type}-R-${i}`,
      code: x.code,
      description: x.Description,
      status: "Suggested",
      recommendation_id: x.recommendation_id,
    })),
  ];

  const addMany = (array, setArray, codes) => {
    let i = 0;
    const copy = [...array];
    for (let c of codes) {
      while (copy[i]?.saved) i++;
      copy[i] = {
        id: i + 1,
        code: c.code,
        description: c.description,
        saved: true,
      };
    }
    setArray(copy);
  };

  return (
    <div className="dictation-medcode">
      <div className="medcode-edit">
        {readOnly ||
          (!edit ? (
            <EditIcon
              color="secondary"
              fontSize={"small"}
              onClick={() => setEdit(true)}
            />
          ) : (
            <div className="btn-cont">
              <button onClick={onSave} className="med-save">
                {isSaving ? <ButtonProgress size={14} /> : "Save"}
              </button>
              <button onClick={() => setEdit(false)} className="med-cancel">
                Cancel
              </button>
            </div>
          ))}
      </div>

      {medCodeError && <div className="med-error">{medCodeError}</div>}
      {loading && <LinearProgress color="secondary" />}
      <Box
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Typography>Medical Codes</Typography>
        <Button onClick={() => setAddType("Diagnosis")} disabled={!edit}>
          Add Diagnosis
        </Button>
      </Box>
      <MedCodesGrid
        rows={buildRows(savedDiag, suggDiag, "ICD")}
        editable={edit}
        onDelete={(r) => onDelete("Diagnosis", r.raw.id)}
        onAccept={(r) =>
          updateSuggestedCodeStatus(
            "Diagnosis",
            r.raw.id,
            "Active",
            r.recommendation_id,
          )
        }
        onReject={(r) =>
          updateSuggestedCodeStatus(
            "Diagnosis",
            r.raw.id,
            "Reject",
            r.recommendation_id,
          )
        }
      />
      <Box
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Typography>Recommended CPT Codes</Typography>
        <Button onClick={() => setAddType("Procedure")} disabled={!edit}>
          Add CPT
        </Button>
      </Box>
      <MedCodesGrid
        rows={buildRows(savedProce, suggProce, "CPT")}
        editable={edit}
        onAccept={(r) =>
          updateSuggestedCodeStatus(
            "Procedure",
            r.raw.id,
            "Active",
            r.recommendation_id,
          )
        }
        onReject={(r) =>
          updateSuggestedCodeStatus(
            "Procedure",
            r.raw.id,
            "Reject",
            r.recommendation_id,
          )
        }
      />
      <Box
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Typography>Recommended ICD Codes</Typography>
        <Button onClick={() => setAddType("Modifier")} disabled={!edit}>
          Add Modifier
        </Button>
      </Box>
      <MedCodesGrid
        rows={buildRows(savedModifier, [], "MOD")}
        editable={edit}
        onAccept={(r) =>
          updateSuggestedCodeStatus(
            "Modifier",
            r.raw.id,
            "Active",
            r.recommendation_id,
          )
        }
        onReject={(r) =>
          updateSuggestedCodeStatus(
            "Modifier",
            r.raw.id,
            "Reject",
            r.recommendation_id,
          )
        }
      />

      <AddCodesDialog
        open={!!addType}
        type={addType}
        onClose={() => setAddType(null)}
        onAdd={(codes) => {
          if (addType === "Diagnosis") addMany(savedDiag, setSavedDiag, codes);
          if (addType === "Procedure")
            addMany(savedProce, setSavedProce, codes);
          if (addType === "Modifier")
            addMany(savedModifier, setSavedModifier, codes);
        }}
      />

      {showText && (
        <ShowText
          text={details.ai_rec_cpt_codes_text}
          onClose={() => setShowText(false)}
        />
      )}
      {showLinkText && (
        <ShowText
          text={details.ai_rec_icd_codes_text}
          onClose={() => setShowLinkText(false)}
        />
      )}
    </div>
  );
};

export default memo(NonAiMedCodes);
